/**
 * Campaign picker of the Test Runs screen (epic 031 task 06): a select over
 * the four testable campaign styles, one optgroup per style (the groups keep a
 * fixed, Chelsie-facing order), each option `"<campaign name>"`. The selected
 * campaign's lifecycle status renders as a status pill beside the select
 * (native `<option>` elements cannot carry markup). Selecting navigates to
 * `/test-runs/<id>`.
 */

import type { CampaignDetailResponse } from '../../lib/api';
import type { CampaignStyle } from '@leads-platform/shared';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { StatusPill } from '../../components/ui/status-pill';

/** The style optgroups in display order (campaigns.tsx terminology). */
const STYLE_GROUPS: ReadonlyArray<readonly [CampaignStyle, string]> = [
  ['co_op', 'Co-op'],
  ['custom_optin', 'Custom Opt-in'],
  ['cto_email', 'Email CTO'],
  ['paid_social', 'Paid Social'],
];

interface CampaignPickerProps {
  campaigns: CampaignDetailResponse[];
  selectedId: string | null;
  onSelect: (campaignId: string) => void;
}

export function CampaignPicker({ campaigns, selectedId, onSelect }: CampaignPickerProps) {
  const selected = selectedId === null ? null : campaigns.find((c) => c.id === selectedId);

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="w-80">
        <Label htmlFor="test-runs-campaign" className="mb-1 block text-xs">
          Campaign
        </Label>
        <Select
          id="test-runs-campaign"
          value={selectedId ?? ''}
          onChange={(e) => {
            if (e.target.value !== '') onSelect(e.target.value);
          }}
        >
          <option value="">Pick a campaign to test…</option>
          {STYLE_GROUPS.map(([style, label]) => {
            const rows = campaigns.filter((campaign) => campaign.campaignStyle === style);
            if (rows.length === 0) return null;
            return (
              <optgroup key={style} label={label}>
                {rows.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </optgroup>
            );
          })}
        </Select>
      </div>
      {selected ? (
        <div className="pb-2.5">
          <StatusPill value={selected.status} />
        </div>
      ) : null}
    </div>
  );
}
